"use strict";

var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.ColorPickerInput = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _ClickAwayListener = _interopRequireDefault(require("@material-ui/core/ClickAwayListener"));
var _web = require("@react-hookz/web");
var _lodestarIcons = require("@smartsheet/lodestar-icons");
var _lodestarUtils = require("@smartsheet/lodestar-utils");
var _loggingStateService = require("@smartsheet/logging-state-service");
var _react = _interopRequireWildcard(require("react"));
var _i18n = require("../../../i18n/i18n");
var _useTheme = require("../../../theme/utils/useTheme");
var _logging = require("../../../util/logging");
var _flyout = require("../../flyout");
var _focusMode = require("../../focusMode");
var _TextInput = require("../../textinput/TextInput");
var _ColorPicker = require("../ColorPicker");
var _CustomColorPicker = require("../CustomColorPicker");
var _colorUtilities = require("../colorPickerUtils/colorUtilities");
var _warnOnLocaleProp = require("../colorPickerUtils/warnOnLocaleProp");
var _ColorPickerCatalogs = require("../i18n/ColorPickerCatalogs");
var _styled = require("./styled");
var _UrsaColorPickerInputButton = require("./styled/UrsaColorPickerInputButton");
const warnOnNoLocaleProp = (0, _warnOnLocaleProp.makeWarnOnLocaleProp)("ColorPickerInput");

// Fancy props (i.e. w/ the fancy object return)

// Union'd type that can discriminate based on the type given to the
// react component when it's used!
const isNormal = props => {
  return props.forceReturnType !== "full";
};
const ColorPickerInput = (0, _i18n.withTranslationInjection)(props => {
  const {
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledby,
    "aria-describedby": ariaDescribedby,
    className,
    color,
    clientId,
    automaticColor,
    showCustom,
    customColors,
    id,
    onCustomColorsChange,
    locale,
    pickerType = "default",
    flyoutProps: providedFlyoutProps = {},
    showOpacity = false,
    isDisabled,
    standardColorPalette,
    maxCustomColors
  } = props;
  if (process.env.NODE_ENV !== "production") {
    if (typeof props.locale !== "string") {
      warnOnNoLocaleProp();
    }
  }
  const [isValid, setIsValid] = (0, _react.useState)((0, _colorUtilities.validateHexCode)(color, showOpacity));
  const [colorValue, setColorValue] = (0, _react.useState)(color);
  const [lastColor, setLastColor] = (0, _react.useState)((0, _colorUtilities.colorParser)(color).hex);
  const [showPicker, setShowPicker] = (0, _react.useState)(false);
  const [displayValue, setDisplayValue] = (0, _react.useState)((0, _colorUtilities.colorParser)(color).hex);
  const [isFocused, setIsFocused] = (0, _react.useState)(false);
  const regex = new RegExp(`[^#0-9A-Fa-f]`);
  const logger = (0, _react.useContext)(_loggingStateService.LoggerContext);
  const withLogging = new _logging.LoggingDecorator(logger, {
    clientId,
    controlType: "cpi"
  });
  const loggerContent = {
    actionId: _loggingStateService.LogActionId.CLICK
  };
  const dispatchChange = value => {
    const colorDefs = (0, _colorUtilities.colorParser)(value);
    const {
      rgba,
      hex,
      a
    } = colorDefs;
    switch (props.forceReturnType) {
      case "rgba":
        void withLogging.enabledByDefault(loggerContent, props.onChange)(rgba);
        break;
      case "hex":
        void withLogging.enabledByDefault(loggerContent, props.onChange)((0, _colorUtilities.rgb2hex)({
          ...colorDefs
        }));
        break;
      case "full":
        void withLogging.enabledByDefault(loggerContent, props.onChange)(colorDefs);
        break;
      default:
        {
          const clr = showOpacity && a !== undefined && a > -1 ? rgba : hex;
          void withLogging.enabledByDefault(loggerContent, props.onChange)(clr);
        }
    }
  };
  const textChangeHandler = value => {
    // This is to restrict the input to valid hex code characters only
    const newValue = value.replace(regex, "");
    setColorValue(newValue);
    setDisplayValue(newValue);
    const isNewValueValid = (0, _colorUtilities.validateHexCode)(newValue, showOpacity);
    setIsValid(isNewValueValid);
    if (isNewValueValid) {
      setLastColor(newValue);
      dispatchChange(newValue);
    }
  };
  const pickerChangeHandler = value => {
    setDisplayValue(value.hex);
    if (isNormal(props)) {
      setColorValue(value.hex);
      setLastColor(value.hex);
    } else {
      setColorValue(value.rgba);
      setLastColor(value.rgba);
    }
    dispatchChange(value.rgba);
  };
  const onCancel = () => {
    setShowPicker(false);
    void withLogging.enabledByDefault(loggerContent, () => {})();
  };
  const blurHandler = () => {
    setIsFocused(false);
    if (!isValid) {
      const prevHex = (0, _colorUtilities.colorParser)(lastColor).hex;
      setDisplayValue(prevHex);
      setColorValue(prevHex);
      if ((0, _colorUtilities.validateHexCode)(prevHex, showOpacity)) {
        setIsValid(true);
      }
    }
  };
  const openPicker = () => {
    window.addEventListener("keydown", checkForEscape);
    setShowPicker(true);
  };
  const checkForEscape = event => {
    if (event.key === "Escape") {
      event.stopPropagation();
      closePicker();
    } else {
      return event;
    }
  };
  const closePicker = () => {
    window.removeEventListener("keydown", checkForEscape);
    setShowPicker(false);
  };
  const prevProp = (0, _web.usePrevious)({
    color
  });
  (0, _react.useEffect)(() => {
    if (prevProp?.color !== props.color) {
      const newColor = (0, _colorUtilities.colorParser)(props.color).hex;
      setDisplayValue(newColor);
      setLastColor(newColor);
      setColorValue(props.color);
    }
  }, [props, prevProp]);
  const {
    offset,
    ...rest
  } = providedFlyoutProps;
  const {
    flyoutProps,
    targetProps
  } = (0, _flyout.useFlyout)({
    placement: "bottom-start",
    closeOnEscape: false,
    ...rest,
    isOpen: showPicker,
    onCloseRequested: closePicker,
    offset: {
      distance: 16,
      skidding: -134,
      ...offset
    }
  });
  const {
    key
  } = (0, _useTheme.useTheme)();
  const themeMode = key;
  const {
    focusMode
  } = (0, _focusMode.useFocusMode)();
  const dotColor = isValid ? colorValue : color;
  const getThemedButton = i18n => {
    switch (themeMode) {
      case "ursa-light":
        return /*#__PURE__*/_react.default.createElement(_UrsaColorPickerInputButton.UrsaColorPickerInputButton, (0, _extends2.default)({}, targetProps, {
          onClick: isDisabled ? undefined : openPicker,
          "data-client-id": (0, _lodestarUtils.getClientId)(clientId, "link-btn"),
          appearance: "secondary-empty",
          size: "xsmall",
          isDisabled: isDisabled
        }), i18n._( /*i18n*/"Change"));
      case "lodestar-light":
        return /*#__PURE__*/_react.default.createElement(_styled.ColorPickerInputButton, (0, _extends2.default)({}, targetProps, {
          onClick: isDisabled ? undefined : openPicker,
          "data-client-id": (0, _lodestarUtils.getClientId)(clientId, "link-btn"),
          themeMode: themeMode,
          "aria-disabled": isDisabled,
          type: "button"
        }), i18n._( /*i18n*/"Change"));
    }
  };
  return /*#__PURE__*/_react.default.createElement(_i18n.I18nContextProvider, {
    locale: locale
  }, /*#__PURE__*/_react.default.createElement(_i18n.I18NContextConsumer, null, _ref => {
    let {
      i18n
    } = _ref;
    return /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, /*#__PURE__*/_react.default.createElement(_styled.ColorPickerInputRoot, {
      className: className,
      "data-client-id": clientId,
      focusMode: focusMode,
      isFocused: isFocused,
      themeMode: themeMode,
      validationState: isValid ? undefined : "error"
    }, /*#__PURE__*/_react.default.createElement(_TextInput.TextInput, {
      role: "textbox",
      value: displayValue,
      onChange: textChangeHandler,
      "data-client-id": (0, _lodestarUtils.getClientId)(clientId, "input"),
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledby,
      "aria-describedby": ariaDescribedby,
      "aria-invalid": !isValid,
      id: id,
      onBlur: blurHandler,
      isDisabled: isDisabled,
      onFocus: () => setIsFocused(true)
    }), /*#__PURE__*/_react.default.createElement(_styled.ColorPickerInputDot, {
      style: {
        backgroundColor: dotColor,
        color: dotColor
      },
      onClick: isDisabled ? undefined : openPicker,
      "data-client-id": (0, _lodestarUtils.getClientId)(clientId, "dot-btn"),
      themeMode: themeMode,
      "aria-disabled": isDisabled,
      "aria-hidden": true,
      tabIndex: -1,
      type: "button"
    }), getThemedButton(i18n), !isValid && /*#__PURE__*/_react.default.createElement(_styled.ColorPickerInputErrorMessage, {
      themeMode: themeMode,
      "data-client-id": (0, _lodestarUtils.getClientId)(clientId, "error-msg")
    }, /*#__PURE__*/_react.default.createElement(_lodestarIcons.AlertErrorAltIcon, null), i18n._( /*i18n*/{
      id: "Invalid hex value"
    }))), /*#__PURE__*/_react.default.createElement(_ClickAwayListener.default, {
      onClickAway: closePicker,
      mouseEvent: "onMouseDown"
    }, /*#__PURE__*/_react.default.createElement(_flyout.Flyout, (0, _extends2.default)({}, flyoutProps, {
      closeOnEscape: false
    }), pickerType === "default" ? /*#__PURE__*/_react.default.createElement(_ColorPicker.ColorPicker, {
      showOpacity: showOpacity,
      color: dotColor,
      onChange: pickerChangeHandler,
      clientId: (0, _lodestarUtils.getClientId)(clientId, "color-picker"),
      automaticColor: automaticColor,
      showCustom: showCustom,
      customColors: customColors,
      onCustomColorsChange: onCustomColorsChange,
      locale: locale,
      forceReturnType: "full",
      standardColorPalette: standardColorPalette,
      maxCustomColors: maxCustomColors
    }) : /*#__PURE__*/_react.default.createElement(_CustomColorPicker.CustomColorPicker, {
      showOpacity: showOpacity,
      color: dotColor,
      onApply: value => {
        pickerChangeHandler(value);
        setShowPicker(false);
      },
      onCancel: onCancel,
      clientId: (0, _lodestarUtils.getClientId)(clientId, "color-picker"),
      locale: locale,
      forceReturnType: "full"
    }))));
  }));
}, _ColorPickerCatalogs.injectCatalog);
exports.ColorPickerInput = ColorPickerInput;
//# sourceMappingURL=ColorPickerInput.js.map